import { z } from 'zod';
import { NOTIFY_CHANNELS } from './notifications.js';

/**
 * Flows: a diagram of tests and groups with a start, conditions, joins and ends (one project).
 *
 * Semantics (shared by the editor checks, the API and the runner's orchestrator):
 * - Start begins the flow. Several connections out of a node run those branches at the same time.
 * - Test / Group nodes run a test or a group (a TestRun each). When they finish, their connections
 *   are followed - after a failure only into Condition nodes, unless the flow continues after failures.
 * - Condition nodes look at results (the step before it, or the whole flow so far) and follow either
 *   their "yes" or their "no" connection.
 * - Join nodes wait for their incoming branches: all of them, or the first one to finish.
 * - Notify nodes send a message (the results so far and/or the user's own text) to the project's
 *   Slack / Teams / Zoho Cliq / email channels, then carry on. Like Conditions they are reached after
 *   a failure too, and pass that failure on: after it, only Conditions and Notify nodes continue
 *   (unless the flow continues after failures). A notification that can't be delivered never fails the flow.
 * - End nodes mark a path as finished. Only Join and End nodes take more than one incoming connection.
 * Paths not taken are SKIPPED. The flow is FAILED if any test or group failed, ERROR if one errored.
 */

export const FLOW_NODE_TYPES = ['start', 'test', 'group', 'condition', 'join', 'notify', 'end'] as const;
export type FlowNodeType = (typeof FLOW_NODE_TYPES)[number];

export const CONDITION_RULES = ['all_passed', 'any_failed', 'pass_rate_at_least', 'test_failed'] as const;
export type ConditionRule = (typeof CONDITION_RULES)[number];
export const CONDITION_SCOPES = ['previous', 'flow'] as const;
export type ConditionScope = (typeof CONDITION_SCOPES)[number];
export const JOIN_MODES = ['all', 'any'] as const;
export type JoinMode = (typeof JOIN_MODES)[number];
/** When a Notify step sends: always, only when something failed, or only when everything passed (scope: its "Results of"). */
export const NOTIFY_WHEN = ['always', 'failed', 'passed'] as const;
export type NotifyWhen = (typeof NOTIFY_WHEN)[number];
export const MAX_NOTIFY_EMAILS = 20;
export const BRANCHES = ['yes', 'no'] as const;
export type Branch = (typeof BRANCHES)[number];

export const MAX_FLOW_NODES = 100;
export const MAX_FLOW_EDGES = 300;

const id = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);
const base = {
  id,
  position: z.object({ x: z.number().finite(), y: z.number().finite() }),
  /** Optional caption shown on the node instead of its default title. */
  label: z.string().trim().max(80).optional(),
};

export const flowNodeSchema = z.discriminatedUnion('type', [
  z.object({ ...base, type: z.literal('start'), data: z.object({}).strict().default({}) }),
  z.object({ ...base, type: z.literal('test'), data: z.object({ testCaseId: z.string().max(64).default('') }) }),
  z.object({ ...base, type: z.literal('group'), data: z.object({ groupId: z.string().max(64).default('') }) }),
  z.object({
    ...base,
    type: z.literal('condition'),
    data: z.object({
      rule: z.enum(CONDITION_RULES).default('all_passed'),
      scope: z.enum(CONDITION_SCOPES).default('previous'),
      /** pass_rate_at_least: percent 1-100. */
      threshold: z.number().int().min(1).max(100).optional(),
      /** test_failed: the test to look at (its latest result in this flow run). */
      testCaseId: z.string().max(64).optional(),
    }),
  }),
  z.object({ ...base, type: z.literal('join'), data: z.object({ wait: z.enum(JOIN_MODES).default('all') }) }),
  z.object({
    ...base,
    type: z.literal('notify'),
    data: z.object({
      /** Where to send: the project's connected notification channels (Plugins page). */
      channels: z.array(z.enum(NOTIFY_CHANNELS)).max(NOTIFY_CHANNELS.length).default([]),
      /** Email recipients for this step; empty = the email plugin's recipients. Checked by validateFlow. */
      emails: z.array(z.string().trim().max(254)).max(MAX_NOTIFY_EMAILS).default([]),
      when: z.enum(NOTIFY_WHEN).default('always'),
      /** Add the results (counts and failed tests) of `scope`. */
      includeResults: z.boolean().default(true),
      scope: z.enum(CONDITION_SCOPES).default('flow'),
      /** Title / email subject; default says the flow and its result. */
      subject: z.string().trim().max(150).optional(),
      /** The user's own text, shown above the results. */
      message: z.string().trim().max(2000).optional(),
    }),
  }),
  z.object({ ...base, type: z.literal('end'), data: z.object({}).strict().default({}) }),
]);
export type FlowNode = z.infer<typeof flowNodeSchema>;

export const flowEdgeSchema = z.object({
  id,
  source: id,
  target: id,
  /** Only on connections out of a Condition node. */
  branch: z.enum(BRANCHES).optional(),
});
export type FlowEdge = z.infer<typeof flowEdgeSchema>;

export const flowGraphSchema = z.object({
  nodes: z.array(flowNodeSchema).max(MAX_FLOW_NODES),
  edges: z.array(flowEdgeSchema).max(MAX_FLOW_EDGES),
});
export type FlowGraph = z.infer<typeof flowGraphSchema>;

export const flowInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).nullish(),
  /** Follow every connection after a failed test/group (default: only into Condition nodes). */
  continueOnFailure: z.boolean().default(false),
  graph: flowGraphSchema,
});
export type FlowInput = z.infer<typeof flowInputSchema>;
export const flowUpdateSchema = flowInputSchema.partial();
export type FlowUpdate = z.infer<typeof flowUpdateSchema>;

/** A new flow: Start -> End, ready to have tests dropped in between. */
export function starterGraph(): FlowGraph {
  return {
    nodes: [
      { id: 'start', type: 'start', position: { x: 0, y: 0 }, data: {} },
      { id: 'end', type: 'end', position: { x: 0, y: 320 }, data: {} },
    ],
    edges: [{ id: 'e-start-end', source: 'start', target: 'end' }],
  };
}

// ---------------------------------------------------------------- checks

export interface FlowProblem {
  message: string;
  nodeId?: string;
  edgeId?: string;
}

export const NODE_TITLES: Record<FlowNodeType, string> = {
  start: 'Start',
  test: 'Test',
  group: 'Group',
  condition: 'Condition',
  join: 'Join',
  notify: 'Notify',
  end: 'End',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Structural problems that stop a flow from running (none = runnable). `refs` checks that tests and
 * groups exist in the project (ids it knows); omit it to check the shape only.
 */
export function validateFlow(graph: FlowGraph, refs?: { testIds: Set<string>; groupIds: Set<string> }): FlowProblem[] {
  const problems: FlowProblem[] = [];
  const byId = new Map<string, FlowNode>();
  for (const n of graph.nodes) {
    if (byId.has(n.id)) problems.push({ nodeId: n.id, message: `Two steps share the id "${n.id}".` });
    byId.set(n.id, n);
  }
  const name = (n: FlowNode) => n.label || NODE_TITLES[n.type];
  const incoming = new Map<string, FlowEdge[]>();
  const outgoing = new Map<string, FlowEdge[]>();
  const seen = new Set<string>();
  for (const e of graph.edges) {
    if (!byId.has(e.source) || !byId.has(e.target)) {
      problems.push({ edgeId: e.id, message: 'A connection points to a step that no longer exists.' });
      continue;
    }
    if (e.source === e.target) problems.push({ edgeId: e.id, message: 'A step cannot connect to itself.' });
    const key = `${e.source}>${e.target}>${e.branch ?? ''}`;
    if (seen.has(key)) problems.push({ edgeId: e.id, message: 'Two connections join the same steps.' });
    seen.add(key);
    incoming.set(e.target, [...(incoming.get(e.target) ?? []), e]);
    outgoing.set(e.source, [...(outgoing.get(e.source) ?? []), e]);
  }

  const starts = graph.nodes.filter((n) => n.type === 'start');
  if (starts.length !== 1) problems.push({ message: starts.length ? 'A flow has exactly one Start.' : 'Add a Start step.' });
  if (!graph.nodes.some((n) => n.type === 'end')) problems.push({ message: 'Add an End step.' });

  for (const n of graph.nodes) {
    const ins = incoming.get(n.id) ?? [];
    const outs = outgoing.get(n.id) ?? [];
    const at = (message: string) => problems.push({ nodeId: n.id, message: `${name(n)}: ${message}` });
    if (n.type === 'start') {
      if (ins.length) at('nothing can lead into Start.');
      if (!outs.length) at('connect Start to the first step.');
    } else if (n.type === 'end') {
      if (!ins.length) at('connect a step to this End.');
      if (outs.length) at('End cannot lead anywhere.');
    } else {
      if (!ins.length) at('connect a step into it.');
      if (n.type !== 'join' && ins.length > 1) at('only Join and End steps take more than one incoming connection - add a Join.');
      if (n.type === 'join' && ins.length < 2) at('a Join needs at least two incoming branches.');
      if (!outs.length) at('connect it to the next step or an End.');
    }
    if (n.type === 'condition') {
      if (outs.some((e) => !e.branch)) at('label each connection as Yes or No.');
      if (!outs.some((e) => e.branch === 'yes') || !outs.some((e) => e.branch === 'no')) at('connect both Yes and No.');
      if (n.data.rule === 'pass_rate_at_least' && !n.data.threshold) at('choose the pass rate.');
      if (n.data.rule === 'test_failed' && !n.data.testCaseId) at('choose the test to look at.');
      if (n.data.rule === 'test_failed' && n.data.testCaseId && refs && !refs.testIds.has(n.data.testCaseId)) at('the chosen test no longer exists.');
    } else if (outs.some((e) => e.branch)) {
      at('only Condition steps have Yes/No connections.');
    }
    if (n.type === 'test') {
      if (!n.data.testCaseId) at('choose a test.');
      else if (refs && !refs.testIds.has(n.data.testCaseId)) at('the chosen test no longer exists.');
    }
    if (n.type === 'group') {
      if (!n.data.groupId) at('choose a group.');
      else if (refs && !refs.groupIds.has(n.data.groupId)) at('the chosen group no longer exists.');
    }
    if (n.type === 'notify') {
      if (!n.data.channels.length) at('choose where to send it.');
      if (!n.data.includeResults && !n.data.message) at('write a message or include the results.');
      const bad = n.data.emails.filter((e) => !EMAIL_RE.test(e));
      if (bad.length) at(`${bad.join(', ')} ${bad.length === 1 ? 'is not an email address' : 'are not email addresses'}.`);
    }
  }

  // No loops: a flow always moves forward.
  const color = new Map<string, 1 | 2>();
  let loop = false;
  const visit = (nid: string) => {
    if (loop) return;
    color.set(nid, 1);
    for (const e of outgoing.get(nid) ?? []) {
      const c = color.get(e.target);
      if (c === 1) loop = true;
      else if (!c) visit(e.target);
    }
    color.set(nid, 2);
  };
  for (const n of graph.nodes) if (!color.has(n.id)) visit(n.id);
  if (loop) problems.push({ message: 'The flow loops back on itself. Connections must always move forward.' });

  // Everything reachable from Start.
  if (starts.length === 1) {
    const reach = new Set<string>([starts[0]!.id]);
    const queue = [starts[0]!.id];
    while (queue.length) for (const e of outgoing.get(queue.shift()!) ?? []) if (!reach.has(e.target)) (reach.add(e.target), queue.push(e.target));
    for (const n of graph.nodes) if (!reach.has(n.id)) problems.push({ nodeId: n.id, message: `${name(n)}: not reachable from Start.` });
  }
  return problems;
}

// ---------------------------------------------------------------- running

export const FLOW_NODE_STATUSES = ['PENDING', 'RUNNING', 'PASSED', 'FAILED', 'ERROR', 'CANCELLED', 'SKIPPED'] as const;
export type FlowNodeStatus = (typeof FLOW_NODE_STATUSES)[number];

export interface FlowStats {
  total: number;
  passed: number;
  failed: number;
  errored: number;
  skipped: number;
}
export const EMPTY_STATS: FlowStats = { total: 0, passed: 0, failed: 0, errored: 0, skipped: 0 };
const addStats = (a: FlowStats, b?: FlowStats): FlowStats => ({
  total: a.total + (b?.total ?? 0),
  passed: a.passed + (b?.passed ?? 0),
  failed: a.failed + (b?.failed ?? 0),
  errored: a.errored + (b?.errored ?? 0),
  skipped: a.skipped + (b?.skipped ?? 0),
});

export interface FlowNodeState {
  status: FlowNodeStatus;
  /** Test / Group nodes: the TestRun that ran it. */
  runId?: string;
  /** Condition nodes: the branch taken. */
  outcome?: Branch;
  /** Results counted for conditions (Test/Group: its run; Join: the branches it waited for). */
  stats?: FlowStats;
  /** Short explanation shown on the node (e.g. why a condition chose its branch). */
  message?: string;
  /** Notify nodes: reached after a failure, which they pass on to the connections after them. */
  failedBefore?: boolean;
  startedAt?: string;
  finishedAt?: string;
}

/** FlowRun.state: progress of every step and connection, saved as the flow runs. */
export interface FlowState {
  nodes: Record<string, FlowNodeState>;
  edges: Record<string, 'taken' | 'skipped'>;
}

export function initialFlowState(graph: FlowGraph): FlowState {
  return { nodes: Object.fromEntries(graph.nodes.map((n) => [n.id, { status: 'PENDING' as const }])), edges: {} };
}

export interface AdvanceOptions {
  continueOnFailure: boolean;
  /** Latest status of each test in this flow run (for "this test failed" conditions). */
  testResults: ReadonlyMap<string, string>;
  /** Display names of Test / Group nodes by node id (the test's or group's name), used in skip reasons. */
  names?: ReadonlyMap<string, string>;
  now?: () => Date;
}

const isFailure = (s: FlowNodeStatus | string) => s === 'FAILED' || s === 'ERROR';

/** Whether a condition holds, and why (shown on the node). */
export function evaluateCondition(
  node: Extract<FlowNode, { type: 'condition' }>,
  stats: FlowStats,
  testResults: ReadonlyMap<string, string>,
): { yes: boolean; message: string } {
  const d = node.data;
  const bad = stats.failed + stats.errored;
  const where = d.scope === 'flow' ? 'in the flow so far' : 'in the previous step';
  switch (d.rule) {
    case 'all_passed':
      return { yes: stats.total > 0 && bad === 0, message: `${stats.passed} of ${stats.total} passed ${where}` };
    case 'any_failed':
      return { yes: bad > 0, message: `${bad} of ${stats.total} failed ${where}` };
    case 'pass_rate_at_least': {
      const rate = stats.total ? Math.round((stats.passed / stats.total) * 100) : 0;
      return { yes: stats.total > 0 && rate >= (d.threshold ?? 100), message: `pass rate ${rate}% ${where} (needs ${d.threshold ?? 100}%)` };
    }
    case 'test_failed': {
      const s = d.testCaseId ? testResults.get(d.testCaseId) : undefined;
      return { yes: !!s && isFailure(s), message: s ? `the test ${s === 'PASSED' ? 'passed' : s.toLowerCase()}` : 'the test has not run in this flow' };
    }
  }
}

/** Results of every test and group in the flow so far. */
export function flowStatsSoFar(graph: FlowGraph, state: FlowState): FlowStats {
  return graph.nodes.filter((n) => n.type === 'test' || n.type === 'group').reduce((acc, n) => addStats(acc, state.nodes[n.id]?.stats), EMPTY_STATS);
}

/**
 * What a Notify node that may start now reports on: the step before it (its stats and, for a test or
 * group, its TestRun) or the whole flow so far, and whether it was reached after a failure.
 */
export function notifyContext(
  graph: FlowGraph,
  state: FlowState,
  nodeId: string,
): { stats: FlowStats; previousStats: FlowStats; previousRunId?: string; failedBefore: boolean } {
  const node = graph.nodes.find((n) => n.id === nodeId);
  const edge = graph.edges.find((e) => e.target === nodeId && state.edges[e.id] === 'taken');
  const prev = edge ? state.nodes[edge.source] : undefined;
  const previousStats = prev?.stats ?? EMPTY_STATS;
  const scope = node?.type === 'notify' ? node.data.scope : 'flow';
  return {
    stats: scope === 'flow' ? flowStatsSoFar(graph, state) : previousStats,
    previousStats,
    ...(prev?.runId ? { previousRunId: prev.runId } : {}),
    failedBefore: !!prev && (isFailure(prev.status) || !!prev.failedBefore),
  };
}

/** Whether a Notify node sends for these results, and why not. */
export function notifyDecision(when: NotifyWhen, stats: FlowStats): { send: boolean; reason?: string } {
  const bad = stats.failed + stats.errored;
  if (when === 'failed' && bad === 0) return { send: false, reason: 'Not sent: nothing failed' };
  if (when === 'passed' && (stats.total === 0 || bad > 0)) return { send: false, reason: stats.total ? 'Not sent: something failed' : 'Not sent: no results yet' };
  return { send: true };
}

/**
 * Moves the flow forward as far as it can without running anything: completes Start, Conditions,
 * Joins and Ends, skips paths not taken, and returns the Test / Group / Notify nodes that may start
 * now (they are marked RUNNING). Call again after each of those finishes (completeNode).
 */
export function advanceFlow(graph: FlowGraph, state: FlowState, opts: AdvanceOptions): { state: FlowState; start: string[] } {
  const s: FlowState = structuredClone(state);
  const now = () => (opts.now?.() ?? new Date()).toISOString();
  const incoming = (nid: string) => graph.edges.filter((e) => e.target === nid);
  const outgoing = (nid: string) => graph.edges.filter((e) => e.source === nid);
  const start: string[] = [];
  const settle = (nid: string, patch: Partial<FlowNodeState>, follow: (e: FlowEdge) => boolean) => {
    s.nodes[nid] = { ...s.nodes[nid]!, ...patch, finishedAt: patch.finishedAt ?? now() };
    for (const e of outgoing(nid)) s.edges[e.id] = follow(e) ? 'taken' : 'skipped';
  };
  const flowStats = () => flowStatsSoFar(graph, s);

  let changed = true;
  while (changed) {
    changed = false;
    for (const node of graph.nodes) {
      const st = s.nodes[node.id];
      if (!st || st.status !== 'PENDING') continue;
      if (node.type === 'start') {
        settle(node.id, { status: 'PASSED', stats: EMPTY_STATS, startedAt: now() }, () => true);
        changed = true;
        continue;
      }
      const ins = incoming(node.id);
      const resolved = ins.filter((e) => s.edges[e.id]);
      const taken = ins.filter((e) => s.edges[e.id] === 'taken');
      const allResolved = resolved.length === ins.length;
      if (allResolved && taken.length === 0) {
        settle(node.id, { status: 'SKIPPED', message: skipReason(graph, s, ins, opts.names) }, () => false);
        changed = true;
        continue;
      }
      if (node.type === 'test' || node.type === 'group' || node.type === 'notify') {
        if (taken.length) {
          s.nodes[node.id] = { status: 'RUNNING', startedAt: now() };
          start.push(node.id);
          changed = true;
        }
      } else if (node.type === 'condition') {
        if (taken.length) {
          const stats = node.data.scope === 'flow' ? flowStats() : (s.nodes[taken[0]!.source]?.stats ?? EMPTY_STATS);
          const { yes, message } = evaluateCondition(node, stats, opts.testResults);
          const outcome: Branch = yes ? 'yes' : 'no';
          settle(node.id, { status: 'PASSED', outcome, message, stats, startedAt: now() }, (e) => e.branch === outcome);
          changed = true;
        }
      } else if (node.type === 'join') {
        const ready = node.data.wait === 'any' ? taken.length > 0 : allResolved && taken.length > 0;
        if (ready) {
          const stats = taken.reduce((acc, e) => addStats(acc, s.nodes[e.source]?.stats), EMPTY_STATS);
          const message = node.data.wait === 'any' ? 'continued after the first branch' : `waited for ${taken.length} branch${taken.length === 1 ? '' : 'es'}`;
          settle(node.id, { status: 'PASSED', stats, message, startedAt: now() }, () => true);
          changed = true;
        }
      } else if (node.type === 'end') {
        if (taken.length) {
          settle(node.id, { status: 'PASSED', startedAt: now() }, () => true);
          changed = true;
        }
      }
    }
  }
  return { state: s, start };
}

/**
 * Why a step was not run, from the connections into it (all skipped): the Condition that chose the other
 * branch, the step before that failed, or - when the step before was not run either - that step's reason.
 */
export function skipReason(graph: FlowGraph, state: FlowState, ins: FlowEdge[], names?: ReadonlyMap<string, string>): string {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const nameOf = (n: FlowNode) => names?.get(n.id) || n.label || NODE_TITLES[n.type];
  const reasons = ins.map((e) => {
    const src = byId.get(e.source);
    const st = state.nodes[e.source];
    if (!src || !st) return 'Not run: the step before it did not finish.';
    if (st.status === 'SKIPPED') return st.message ?? `Not run: the step before it, “${nameOf(src)}”, was not run.`;
    if (src.type === 'condition' && st.outcome) {
      const answer = st.outcome === 'yes' ? 'Yes' : 'No';
      const other = e.branch === 'yes' ? 'Yes' : 'No';
      const cond = src.label ? `the condition “${src.label}”` : 'the Condition step before it';
      return `Not run: ${cond} answered ${answer}${st.message ? ` (${st.message})` : ''}, so its ${other} path was not taken.`;
    }
    if (src.type === 'notify' && st.failedBefore) {
      return `Not run: a step before “${nameOf(src)}” failed, and this flow is not set to continue after failures (only Condition and Notify steps continue the path).`;
    }
    if (isFailure(st.status)) {
      const what = st.status === 'ERROR' ? 'stopped with an error' : 'failed';
      return `Not run: “${nameOf(src)}” ${what}, and this flow is not set to continue after failures (only Condition and Notify steps continue the path).`;
    }
    return `Not run: the path from “${nameOf(src)}” was not followed.`;
  });
  // Prefer a reason that names a decision or a failure over a plain "was not run" chain.
  return reasons.find((r) => !r.includes('was not run.')) ?? reasons[0] ?? 'Not run: no path led to this step.';
}

/**
 * Records a finished Test / Group / Notify node and decides which of its connections are followed: all
 * of them after a pass (or when the flow continues after failures), otherwise only those into
 * Conditions and Notify nodes. A Notify node follows the result it was reached with (`failedBefore`),
 * not its own (a message that could not be delivered is not a test failure).
 */
export function completeNode(
  graph: FlowGraph,
  state: FlowState,
  nodeId: string,
  result: { status: FlowNodeStatus; stats: FlowStats; runId?: string; message?: string; failedBefore?: boolean },
  opts: { continueOnFailure: boolean; now?: () => Date },
): FlowState {
  const s: FlowState = structuredClone(state);
  const kinds = new Map(graph.nodes.map((n) => [n.id, n.type]));
  s.nodes[nodeId] = { ...s.nodes[nodeId]!, ...result, finishedAt: (opts.now?.() ?? new Date()).toISOString() };
  const notify = kinds.get(nodeId) === 'notify';
  const passed = notify ? !result.failedBefore : result.status === 'PASSED';
  const failed = notify ? !!result.failedBefore : isFailure(result.status);
  for (const e of graph.edges.filter((x) => x.source === nodeId)) {
    const target = kinds.get(e.target);
    const follow = passed || (failed && (opts.continueOnFailure || target === 'condition' || target === 'notify'));
    s.edges[e.id] = follow ? 'taken' : 'skipped';
  }
  return s;
}

/** True when nothing is running and nothing can start any more. */
export function flowFinished(state: FlowState): boolean {
  return Object.values(state.nodes).every((n) => n.status !== 'PENDING' && n.status !== 'RUNNING');
}

/** Overall result: FAILED if any test/group failed, ERROR if one errored, else PASSED. Notify nodes don't count (pass `graph`). */
export function flowOutcome(state: FlowState, graph?: FlowGraph): 'PASSED' | 'FAILED' | 'ERROR' {
  const notify = new Set(graph?.nodes.filter((n) => n.type === 'notify').map((n) => n.id));
  const all = Object.entries(state.nodes)
    .filter(([id]) => !notify.has(id))
    .map(([, n]) => n);
  if (all.some((n) => n.status === 'FAILED')) return 'FAILED';
  if (all.some((n) => n.status === 'ERROR')) return 'ERROR';
  return 'PASSED';
}

/** Marks everything not finished as CANCELLED (run cancelled or the orchestrator crashed). */
export function cancelFlowState(state: FlowState, now = new Date()): FlowState {
  const s: FlowState = structuredClone(state);
  for (const [nid, n] of Object.entries(s.nodes)) {
    if (n.status === 'PENDING' || n.status === 'RUNNING') s.nodes[nid] = { ...n, status: 'CANCELLED', finishedAt: now.toISOString() };
  }
  return s;
}

/** Reads FlowRun.state defensively. */
export function parseFlowState(value: unknown, graph: FlowGraph): FlowState {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return initialFlowState(graph);
  const v = value as Partial<FlowState>;
  return { nodes: { ...initialFlowState(graph).nodes, ...(v.nodes ?? {}) }, edges: { ...(v.edges ?? {}) } };
}
